import { useAuth, useOrganization } from "@clerk/vue";
import { watch } from "vue";
import type { NavigationGuardNext, RouteLocationNormalized } from "vue-router";
import { useAccount } from "@/composables/useAccount";

function isE2E(): boolean {
  // jsdom and e2e builds can bypass auth for route testing
  const flag =
    (import.meta as ImportMeta).env?.VITE_E2E ??
    (typeof process !== "undefined"
      ? (process.env as Record<string, string | undefined>)?.VITE_E2E
      : undefined);
  if (flag === undefined) return false;
  const normalized = String(flag).toLowerCase();
  return normalized !== "0" && normalized !== "false" && normalized.length > 0;
}

/**
 * Returns the appropriate home route based on the user's app role.
 * Uses useAccount() directly to avoid triggering useOrganization() in guards.
 * - Manager (not admin) → weekly digest
 * - Admin or employee → conversation
 */
export function getRoleHome(): string {
  try {
    const account = useAccount();
    const isAdmin = account.state.value?.employee?.isAdmin === true;
    const hasDirectReports =
      account.state.value?.employee?.hasDirectReports ?? false;
    const canManage = hasDirectReports || isAdmin;

    if (canManage && !isAdmin) {
      return "/dashboard/team/weekly-digest";
    }
  } catch {
    // account not available yet
  }
  return "/chat";
}

export function authGuard(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext,
) {
  // If Clerk isn't available yet (e.g., app just booted), do not force a redirect
  // for routes that would immediately resolve post-initialization. Instead, allow
  // the app to render and let SignedIn/SignedOut handle the interim state.
  // If user hits the login route while already signed in, bounce to redirect target or a default
  try {
    const { isLoaded, isSignedIn } = useAuth();
    if (to.name === "login" && isLoaded.value && isSignedIn.value) {
      const redirect = to.query?.redirect as string | undefined;
      next(redirect || getRoleHome());
      return;
    }
  } catch {
    // ignore; fallback logic below covers unauthenticated cases
  }

  if (!to.meta?.requiresAuth) {
    next();
    return;
  }

  if (isE2E()) {
    next();
    return;
  }

  try {
    const { isLoaded, isSignedIn } = useAuth();
    // Defer decision until Clerk loads to avoid spurious redirects on reload
    if (!isLoaded.value) {
      next();
      return;
    }
    if (!isSignedIn.value) {
      next({ name: "login", query: { redirect: to.fullPath } });
      return;
    }
    next();
  } catch {
    // Fallback if useAuth cannot be used outside setup(). Try global Clerk if available.
    const clerk =
      (globalThis as unknown as { Clerk?: unknown }).Clerk ||
      (typeof window !== "undefined"
        ? ((window as unknown as { Clerk?: unknown }).Clerk as unknown)
        : undefined);
    const maybeClerk = clerk as
      | { user?: unknown; session?: unknown }
      | undefined;
    const signedIn = Boolean(maybeClerk?.user || maybeClerk?.session);
    if (!signedIn) {
      next({ name: "login", query: { redirect: to.fullPath } });
      return;
    }
    next();
  }
}

/**
 * Wait for account state to be initialized (fetched or restored from cache).
 * Returns immediately if already initialized; otherwise waits up to 5 seconds.
 */
function waitForAccount(): Promise<void> {
  const account = useAccount();
  if (account.initialized.value || account.state.value) {
    return Promise.resolve();
  }
  return new Promise<void>((resolve) => {
    const timeout = setTimeout(() => {
      unwatch();
      resolve();
    }, 5000);
    const unwatch = watch(
      () => account.initialized.value || account.state.value !== null,
      (ready) => {
        if (ready) {
          clearTimeout(timeout);
          unwatch();
          resolve();
        }
      },
      { immediate: true },
    );
  });
}

const ONBOARDING_EXEMPT_ROUTES = ["onboarding", "invite-accept", "join"];

/**
 * Prevents navigation away from onboarding until the user completes all steps.
 * Users who have a company but haven't clicked "Launch" are redirected back.
 */
export async function onboardingGuard(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext,
) {
  // Don't block routes that are part of the onboarding/invite flow
  if (ONBOARDING_EXEMPT_ROUTES.includes(to.name as string)) {
    next();
    return;
  }

  // Don't block routes that don't require auth (login, signup, etc.)
  if (!to.meta?.requiresAuth) {
    next();
    return;
  }

  if (isE2E()) {
    next();
    return;
  }

  try {
    const account = useAccount();
    await waitForAccount();

    if (account.needsOnboarding.value) {
      next({ name: "onboarding" });
      return;
    }

    next();
  } catch {
    next();
  }
}

export async function roleGuard(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext,
) {
  const requiredRole = to.meta?.requiresRole as string | undefined;
  const requiresClerkAdmin = to.meta?.requiresClerkAdmin as boolean | undefined;

  if (!requiredRole && !requiresClerkAdmin) {
    next();
    return;
  }

  // Skip role check in E2E mode
  if (isE2E()) {
    next();
    return;
  }

  try {
    const account = useAccount();

    // Wait for account data before checking roles — prevents the race condition
    // where users can briefly access protected pages before redirect kicks in.
    await waitForAccount();

    const home = getRoleHome();

    // Check Clerk admin requirement via useOrganization (real-time membership data,
    // not the JWT-cached orgRole from useAuth which can be stale after role changes)
    if (requiresClerkAdmin) {
      let isOrgAdmin = false;
      try {
        const { membership } = useOrganization();
        isOrgAdmin = membership.value?.role === "org:admin";
      } catch {
        // Fallback if useOrganization cannot be used outside setup().
        // Mirror the authGuard pattern: check global Clerk instance directly.
        const clerk =
          (globalThis as unknown as { Clerk?: unknown }).Clerk ||
          (typeof window !== "undefined"
            ? ((window as unknown as { Clerk?: unknown }).Clerk as unknown)
            : undefined);
        const maybeClerk = clerk as
          | {
              user?: {
                organizationMemberships?: Array<{ role: string }>;
              };
            }
          | undefined;
        isOrgAdmin =
          maybeClerk?.user?.organizationMemberships?.some(
            (m) => m.role === "org:admin",
          ) ?? false;
      }
      if (!isOrgAdmin) {
        next(home);
        return;
      }
    }

    // Check app role requirements (derived from DB, not Clerk metadata)
    const isAdmin = account.state.value?.employee?.isAdmin === true;
    const hasDirectReports =
      account.state.value?.employee?.hasDirectReports ?? false;

    // "manager" routes require direct reports — admin alone is not sufficient
    if (requiredRole === "manager" && !hasDirectReports) {
      next(home);
      return;
    }

    // "managerOrAdmin" gates routes that any admin OR any user with direct
    // reports can access (e.g. pulse creation hub — admins without reports
    // still need access). Distinct from "manager" which excludes admins.
    if (requiredRole === "managerOrAdmin" && !isAdmin && !hasDirectReports) {
      next(home);
      return;
    }

    if (requiredRole === "admin" && !isAdmin) {
      next(home);
      return;
    }

    next();
  } catch {
    // If we can't check roles, allow navigation rather than blocking
    // The component will handle permissions once account loads
    next();
  }
}
